import React, { useCallback, useContext } from 'react';
import { GoogleButtonBlockView, GoogleButtonBlockViewProps } from './GoogleButtonBlock.view';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { mapSelectedProfilesValues } from '../helpers/mapSelectedProfilesValues';
import { LinkGoogleCallToAction } from '../../../models/graphql';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { updateLinkPartial } from '../TextEditor/helpers/withLinks';

export type GoogleButtonBlockControllerProps = {
  // extend your GoogleButtonBlockViewProps
  formInstance: GoogleButtonBlockViewProps['formInstance'];
};

export const GoogleButtonBlockController = (props: GoogleButtonBlockControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { state, dispatch, profiles: socialProfiles, editors } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts } = postGroup;
  const link =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.link
      : postGroup.groupPostAttributes.content.link;
  const selectedProfiles =
    typeof customizeIndex === 'number' && customizeContentPerProfile
      ? socialProfiles.filter(profile => profile.id === posts[customizeIndex].customizedProfileId)
      : mapSelectedProfilesValues(postGroup.profiles, socialProfiles);
  const hasGoogleSelectedProfiles =
    customizeContentPerProfile &&
    typeof customizeIndex === 'number' &&
    posts[customizeIndex] &&
    posts[customizeIndex].customizedProfileId
      ? !!selectedProfiles.find(
          item => item.id === posts[customizeIndex].customizedProfileId && item.__typename === 'GoogleSocialProfile'
        )
      : selectedProfiles.reduce((prev, current) => {
          if (prev) return prev;
          return current.__typename === 'GoogleSocialProfile';
        }, false);

  const disabled = !link;

  const googleButtonValue = link?.googleCallToAction ?? LinkGoogleCallToAction.NONE;

  const postGroupName =
    typeof customizeIndex === 'number' && customizeContentPerProfile
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}`
      : `postGroup.${postGroupIndex}`;
  const selectInputName = `${postGroupName}.content.link.googleButton`;
  const [editorRefs] = editors;

  const isLinkGoogleCallToActionGuard = (value: FormElementValueType): value is LinkGoogleCallToAction => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in LinkGoogleCallToAction;
  };

  const updateGoogleButtonHandler: FormElementAction = useCallback(
    (value, complete) => {
      if (!isLinkGoogleCallToActionGuard(value)) throw new Error('Value is not a LinkGoogleCallToAction enum');
      complete();
      editorRefs.current[postGroupIndex].forEach(editor => {
        updateLinkPartial(editor.editor, {
          googleCallToAction: value
        });
      });
      dispatch({
        type: 'setGoogleButton',
        payload: { customizeIndex, postGroupIndex, googleCallToAction: value }
      });
    },
    [editorRefs, postGroupIndex, dispatch, customizeIndex]
  );

  const viewProps: GoogleButtonBlockViewProps = {
    ...props,
    disabled,
    updateGoogleButtonHandler,
    googleButtonValue,
    selectInputName
  };
  return hasGoogleSelectedProfiles ? <GoogleButtonBlockView {...viewProps} /> : <></>;
};
